import React, { useEffect, useState } from 'react';
import mixpanel from 'mixpanel-browser';
import styled from 'styled-components';
import Restorative, {
  configuration,
  Player,
  RestorativeProgram,
  ActivitySummary,
  CalendarResponse,
} from '../services/Restorative'; // Update the path accordingly
import Pagination from '../components/Pagination';
import RestorativePlayerListComponent from '../components/RestorativePlayerList/RestorativePlayerList';
import Header from '../components/RestorativePlayerList/Header';
import RestorativeActivitySummary from '../components/RestorativeActivitySummary';
import { isToday } from '../utils/isToday';
import RestorativeResidentDialog from '../components/RestorativeResidentDialog';
import { RestorativeResidentData } from '../components/RestorativeResidentDialog/RestorativeResidentDialog';

const CALENDAR_DATE_UPDATE_DELAY = 800;

const ARROW_NAVIGATION_CLICK_EVENT = {
  name: 'Care_CareUI_Programs_Click',
  data: {
    Action: 'CalendarNavigation',
  },
};

const ADD_PLAYER_CLICK_EVENT = {
  name: 'Care_CareUI_Programs_Click',
  data: {
    Action: 'AddAResidents',
  },
};

const RESTRORATIVE_PLAYER_ADDED_EVENT = {
  name: 'Care_CareUI_AddAResident_Click',
  data: {
    Action: 'Resident added',
  },
};

const PaginationContainer = styled.div`
  margin-top: 10px;
`;

const Loader = styled.div(({ theme }) => {
  const { type } = theme.new;
  return `
    margin-top: 10px;
    margin-bottom: 10px;
    font-size: ${type.size[18]};
    text-align: center;
  `;
});

const getStartDate = (endDate: Date) => {
  const date = new Date(endDate);

  date.setDate(date.getDate() - 6);

  return date;
};

const getDatesBetween = (startDate: Date, endDate: Date) => {
  const dates = [];
  const current = new Date(startDate);
  const end = new Date(endDate);

  while (current <= end) {
    dates.push(new Date(current));
    current.setDate(current.getDate() + 1);
  }

  return dates;
};

const RestorativePlayerList: React.FC = () => {
  const currentDate = new Date();
  const [firstArrowClick, setFirstArrowClick] = useState<boolean>(false);
  const [selectedProgramId, setSelectedProgramId] = useState(-1);
  const [refreshingList, setRefreshingList] = useState<boolean>(false);
  const [players, setPlayers] = useState<Player[]>([]);
  const [showAddPlayerDialog, setShowAddPlayerDialog] = useState(false);
  const [serverAddPlayerErrors, setServerAddPlayerErrors] = useState({});
  const [addingPlayer, setAddingPlayer] = useState(false);
  const [restorativePrograms, setRestorativePrograms] = useState([]);
  const [calendarInfo, setCalendarInfo] = useState<CalendarResponse>({});
  const [startDate, setStartDate] = useState<Date>(getStartDate(currentDate));
  const [endDate, setEndDate] = useState<Date>(currentDate);
  const [dates, setDates] = useState<Date[]>([]);
  const [page, setPage] = useState<number>(0);
  const [totalPlayers, setTotalPlayers] = useState<number>(0);
  const [showDetailsDialog, setShowDetailsDialog] = useState(false);
  const [detailsDialogInfo, setDetailsDialogInfo] = useState<ActivitySummary>();
  const [editingPlayer, setEditingPlayer] = useState<false | RestorativeResidentData>(false);
  const [dialogSavingMessage, setDialogSavingMessage] = useState<string | undefined>(undefined);

  const [searchQuery, setSearchQuery] = useState('');

  const [calendarUpdateTimeout, setCalendarUpdateTimeout] = useState<NodeJS.Timeout | boolean>(false);

  const updateDates = async (difference: number) => {
    const date = new Date(endDate);

    date.setDate(date.getDate() + difference);

    if (firstArrowClick) {
      mixpanel.track(ARROW_NAVIGATION_CLICK_EVENT.name, ARROW_NAVIGATION_CLICK_EVENT.data);
      setFirstArrowClick(false);
    }

    setEndDate(date);
    const newStartDate = getStartDate(date);
    setStartDate(newStartDate);
  };

  const updateCalendar = async (players: Player[]) => {
    if (players.length === 0) {
      return;
    }
    Restorative.getActivityCalendar(players, startDate, endDate)
      .then((response) => {
        setCalendarInfo(response);
      })
      .catch((error) => {
        throw Error(`Error updating activity calendar: ${error}`);
      });
  };

  type GetPlayerParams = {
    offset: number;
    program_id?: number;
    query?: string;
  };

  const fetchData = async () => {
    setRefreshingList(true);

    const params: GetPlayerParams = { offset: page };
    if (selectedProgramId !== -1) {
      params.program_id = selectedProgramId;
    }

    params.query = searchQuery;

    Restorative.getPlayers(params)
      .then((playerResponse) => {
        Restorative.getPrograms()
          .then((programResponse) => {
            setPlayers(playerResponse.players);
            setTotalPlayers(playerResponse.total_count);
            setRestorativePrograms(programResponse.data.programs);
            setRefreshingList(false);
          })
          .catch((error) => {
            setRefreshingList(false);
            throw Error(`Error getting restorative programs: ${error}`);
          });
      })
      .catch((error) => {
        setRefreshingList(false);
        throw Error(`Error getting restorative players: ${error}`);
      });
  };

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (calendarUpdateTimeout) {
      clearTimeout(calendarUpdateTimeout as NodeJS.Timeout);
    }

    const datesBetween = getDatesBetween(startDate, endDate);
    setDates(datesBetween);
    setCalendarUpdateTimeout(
      setTimeout(() => {
        setCalendarUpdateTimeout(false);
        updateCalendar(players);
      }, CALENDAR_DATE_UPDATE_DELAY)
    );
  }, [endDate, players, page, startDate]);

  useEffect(() => {
    if (!startDate || !endDate) {
      return;
    }

    fetchData();
  }, [page, selectedProgramId, searchQuery]);

  const convertPlayerToResident = async (playerId: number, selectedPrograms: RestorativeProgram[]) => {
    setDialogSavingMessage(undefined);
    setAddingPlayer(true);

    const response = await Restorative.updatePlayerRestorativeFlag(playerId, selectedPrograms);

    setAddingPlayer(false);

    if (!response) {
      setServerAddPlayerErrors({ general: ['Something went wrong... please try again later.'] });
    } else {
      mixpanel.track(RESTRORATIVE_PLAYER_ADDED_EVENT.name, {
        ...RESTRORATIVE_PLAYER_ADDED_EVENT.data,
        SelectedPrograms: selectedPrograms,
      });

      setShowAddPlayerDialog(false);
      fetchData();
    }
  };

  const addPlayer = async (firstName: string, lastName: string, selectedPrograms: RestorativeProgram[]) => {
    setDialogSavingMessage(undefined);
    setAddingPlayer(true);

    const response = await Restorative.addPlayer(firstName, lastName, selectedPrograms);

    setAddingPlayer(false);

    if (!response) {
      setServerAddPlayerErrors({ general: ['Something went wrong... please try again later.'] });
    } else {
      mixpanel.track(RESTRORATIVE_PLAYER_ADDED_EVENT.name, {
        ...RESTRORATIVE_PLAYER_ADDED_EVENT.data,
        SelectedPrograms: selectedPrograms,
      });

      setShowAddPlayerDialog(false);
      fetchData();
    }
  };

  const editPlayer = async (
    id: number,
    firstName: string,
    lastName: string,
    selectedPrograms: RestorativeProgram[]
  ) => {
    setDialogSavingMessage(undefined);
    setAddingPlayer(true);

    Restorative.editPlayer(id, firstName, lastName, selectedPrograms)
      .then((response) => {
        if (response && response.status === 200) {
          mixpanel.track(RESTRORATIVE_PLAYER_ADDED_EVENT.name, {
            ...RESTRORATIVE_PLAYER_ADDED_EVENT.data,
            SelectedPrograms: selectedPrograms,
          });

          setAddingPlayer(false);
          setShowAddPlayerDialog(false);
          fetchData();
        }
      })
      .catch((error) => {
        setServerAddPlayerErrors({ general: ['Something went wrong... please try again later.'] });
        setAddingPlayer(false);
        throw Error(`Error editing restorative resident: ${error}`);
      });

    // if (!response) {
    //   setServerAddPlayerErrors({ general: ['Something went wrong... please try again later.'] });
    // } else {
    //   mixpanel.track(RESTRORATIVE_PLAYER_ADDED_EVENT.name, {
    //     ...RESTRORATIVE_PLAYER_ADDED_EVENT.data,
    //     SelectedPrograms: selectedPrograms,
    //   });

    //   setShowAddPlayerDialog(false);
    //   fetchData();
    // }
  };

  const removeFromRestorativeList = async (id: number) => {
    setDialogSavingMessage('Removing from restorative list...');
    setAddingPlayer(true);
    setServerAddPlayerErrors({});

    const response = await Restorative.removePlayerFromRestorative(id);

    setAddingPlayer(false);
    setDialogSavingMessage(undefined);

    if (!response || response.status !== 200) {
      setServerAddPlayerErrors({ general: ['Something went wrong... please try again later.'] });
      return;
    }

    mixpanel.track(RESTRORATIVE_PLAYER_ADDED_EVENT.name, {
      ...RESTRORATIVE_PLAYER_ADDED_EVENT.data,
      Action: 'Removed from restorative list',
    });

    setShowAddPlayerDialog(false);
    setEditingPlayer(false);
    fetchData();
  };

  const displayDetails = (playerId: number, name: string, date: Date) => {
    setShowDetailsDialog(true);

    Restorative.getDailySummary(playerId, date).then((response) => {
      const programDate = new Date(date);
      const title = `${name}'s Daily Summary`;

      setDetailsDialogInfo({
        programs: response,
        date: programDate,
        title: title,
        name: name,
      });
    });
  };

  const handleEditClick = (playerData: RestorativeResidentData) => {
    setEditingPlayer(playerData);
    setShowAddPlayerDialog(true);
  };

  return (
    <div>
      {detailsDialogInfo && (
        <RestorativeActivitySummary
          isOpen={showDetailsDialog}
          closeCallback={() => setShowDetailsDialog(false)}
          {...detailsDialogInfo}
        />
      )}

      <Header
        restorativePrograms={restorativePrograms}
        onSelectProgram={(programId) => {
          setSelectedProgramId(programId);
        }}
        onAddResident={() => {
          mixpanel.track(ADD_PLAYER_CLICK_EVENT.name, ADD_PLAYER_CLICK_EVENT.data);
          setEditingPlayer(false);
          setShowAddPlayerDialog(true);
        }}
        updateSearchQuery={(query) => {
          setSearchQuery(query);
        }}
      ></Header>

      <RestorativePlayerListComponent
        players={players}
        calendarInfo={calendarInfo}
        dates={dates}
        nextEnabled={!isToday(endDate)}
        prevDateCallback={() => {
          updateDates(-1);
        }}
        nextDateCallback={() => {
          updateDates(1);
        }}
        logClickCallback={displayDetails}
        editClickCallback={handleEditClick}
      />

      {refreshingList && (
        <Loader>
          Loading... <i className="fa fa-spin fa-spinner"></i>
        </Loader>
      )}
      <PaginationContainer>
        <Pagination
          currentPage={page + 1}
          total={totalPlayers}
          displayedResults={configuration.playersPerPage}
          callback={(newPage) => {
            setPage(newPage - 1);
          }}
        />
      </PaginationContainer>

      {showAddPlayerDialog && (
        <RestorativeResidentDialog
          isOpen={showAddPlayerDialog}
          closeCallback={() => {
            setServerAddPlayerErrors({});
            setDialogSavingMessage(undefined);
            setShowAddPlayerDialog(false);
          }}
          convertResidentCallback={convertPlayerToResident}
          addResidentCallback={addPlayer}
          editResidentCallback={editPlayer}
          removeFromRestorativeCallback={removeFromRestorativeList}
          saving={addingPlayer}
          savingMessage={dialogSavingMessage}
          backendErrors={serverAddPlayerErrors}
          editPlayer={editingPlayer}
          restorativePrograms={restorativePrograms}
        />
      )}
    </div>
  );
};

export default RestorativePlayerList;
